import { Minus, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useCart } from "../lib/cart";
import { money, type Product } from "../lib/types";

export function ProductImage({ product, size = "card" }: { product: Product; size?: "card" | "detail" }) {
  const image = product.images[0];
  if (!image) {
    return <div className={`product-placeholder ${size}`}><span>Anne<br />Cosette</span><small>Photo coming soon</small></div>;
  }
  return (
    <img
      className={`product-image ${size}`}
      src={size === "detail" ? image.url_large : image.url_medium}
      srcSet={`${image.url_small} 560w, ${image.url_medium} 1120w, ${image.url_large} 2000w`}
      sizes={size === "detail" ? "(max-width: 800px) 100vw, 55vw" : "(max-width: 700px) 50vw, 25vw"}
      alt={image.alt_text || product.name}
      loading="lazy"
    />
  );
}

export function ProductCard({ product }: { product: Product }) {
  const [quickOpen, setQuickOpen] = useState(false);
  return (
    <article className="product-card">
      <Link className="product-card-image" to={`/products/${product.slug}`}><ProductImage product={product} /></Link>
      <div className="product-card-copy">
        <div><Link to={`/products/${product.slug}`}>{product.name}</Link><span>{money(product.price_cents)}</span></div>
        <button className="text-button" onClick={() => setQuickOpen(true)}>Quick view</button>
      </div>
      {product.status === "unavailable" && <span className="product-badge">Taking a tiny break</span>}
      {quickOpen && <QuickView product={product} onClose={() => setQuickOpen(false)} />}
    </article>
  );
}

export function QuickView({ product, onClose }: { product: Product; onClose: () => void }) {
  const { add } = useCart();
  const [size, setSize] = useState(product.sizes.length ? "" : null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function addProduct() {
    if (product.sizes.length && !size) return;
    add(product, size, quantity);
    setAdded(true);
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="quick-view" role="dialog" aria-modal="true" aria-labelledby={`quick-${product.id}`} tabIndex={-1} ref={dialog}>
        <button className="icon-button modal-close" onClick={onClose} aria-label="Close quick view"><X /></button>
        <div className="quick-media"><ProductImage product={product} size="detail" /></div>
        <div className="quick-copy">
          <p className="eyebrow">{product.category.name} · Made to order</p>
          <h2 id={`quick-${product.id}`}>{product.name}</h2>
          <p className="price">{money(product.price_cents)}</p>
          <p>{product.description}</p>
          {product.sizes.length > 0 && (
            <fieldset className="size-picker"><legend>Choose a size</legend><div>
              {product.sizes.map((item) => <button type="button" className={size === item.label ? "selected" : ""} onClick={() => setSize(item.label)} key={item.id}>{item.label}</button>)}
            </div></fieldset>
          )}
          <div className="purchase-row">
            <div className="quantity-picker"><button onClick={() => setQuantity(Math.max(1, quantity - 1))} aria-label="Decrease quantity"><Minus /></button><span>{quantity}</span><button onClick={() => setQuantity(Math.min(5, quantity + 1))} aria-label="Increase quantity"><Plus /></button></div>
            <button className="button primary grow" disabled={product.status !== "published" || (product.sizes.length > 0 && !size)} onClick={addProduct}>{added ? "Added to your bag!" : product.status === "published" ? "Add to bag" : "Unavailable"}</button>
          </div>
          <Link className="details-link" to={`/products/${product.slug}`} onClick={onClose}>See every detail</Link>
        </div>
      </div>
    </div>
  );
}

